// NewReplies — replies from outside the office that THIS person has not read.
//
// v0.269.0 (2026-10-30). Brett: "i will need a some indicators though as i
// have no idea when a response arrives. will need to be on the droppin
// register, inspec register and the inspec alarm." then "yes seen perperson".
//
// A reply is a comment from a client through a link, the report's recipient,
// or a signed-in contact. Each office person has their own marks
// (/api/comment-seen): opening an item's conversation marks its replies seen
// for them only, so Brett reading one does not clear it for anyone else.
//
//   useNewReplies(projectId) — the items in a project with replies this
//     person has not seen. Read when the screen opens, after a mark and when
//     the app comes back to the front. No timer: the database pauses after an
//     hour on purpose and a timer would keep waking it.
//   markRepliesSeen(defectId, comments) — called by DefectConversation once
//     the network's copy of the thread lands.
//   NewReplyChip — the register row's marker.
//   RegisterNewReplies — the register's line above the filters.
//   NewRepliesNotice — the Projects dashboard line.
//
// Client and subcontractor sign-ins see none of this: their own replies are
// not news to them, and the route refuses them.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MessageSquareDot } from 'lucide-react';
import { apiGet } from '../api';
import { theme, btnPrimary, btnSecondary } from '../theme';
import { isClientContactSession } from '../utils/contact-session';
import { getCurrentIdentityKey } from '../utils/outbox-identity.js';

export const REPLIES_SEEN_EVENT = 'droppin:replies-seen';

const REPLY_ROLES = new Set(['guest', 'recipient', 'contact']);

function plural(n, one, many) { return `${n} ${n === 1 ? one : many}`; }

const READ_FAILED = 'Could not check for new replies.';

function isAuthEnd(e) { return !!(e && (e.status === 401 || e.status === 403)); }

// The newest mark already recorded per person + item in this tab, so reopening
// the same thread does not write again.
const recorded = new Map();

/**
 * This person has read `comments` (the thread as shown). Records the newest
 * comment id when the thread holds a reply. A plain fetch, never apiSend: a
 * "seen" queued offline and replayed later would clear a reply that arrived
 * in between. Offline it records nothing and the reply stays new until the
 * thread is opened with a connection.
 */
export async function markRepliesSeen(defectId, comments) {
  if (isClientContactSession()) return;
  const id = Number(defectId);
  if (!Number.isInteger(id) || id <= 0 || !Array.isArray(comments)) return;
  if (!comments.some((c) => c && REPLY_ROLES.has(c.authorRole))) return;
  const ids = comments.map((c) => Number(c && c.id)).filter((n) => Number.isInteger(n) && n > 0);
  if (!ids.length) return;
  const last = Math.max(...ids);
  const key = `${getCurrentIdentityKey() || ''}:${id}`;
  if ((recorded.get(key) || 0) >= last) return;
  let r;
  try {
    r = await fetch('/api/comment-seen', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ defectId: id, lastCommentId: last }),
    });
  } catch (_) {
    return;
  }
  if (!r.ok) {
    console.warn('[DropPin] could not mark replies seen:', r.status);
    return;
  }
  recorded.set(key, last);
  try { window.dispatchEvent(new CustomEvent(REPLIES_SEEN_EVENT, { detail: { defectId: id } })); } catch (_) {}
}

/** Re-run `load` after a mark and when the app comes back to the front. */
function useRereads(load) {
  useEffect(() => {
    const onSeen = () => load();
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    window.addEventListener(REPLIES_SEEN_EVENT, onSeen);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener(REPLIES_SEEN_EVENT, onSeen);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);
}

/**
 * { items: [{ defectId, reportId, replies, latestAt }], byDefect: Map, readErr }
 * for one project. Empty for a client or subcontractor sign-in.
 */
export function useNewReplies(projectId) {
  const [items, setItems] = useState([]);
  const [readErr, setReadErr] = useState('');
  const seq = useRef(0);
  const skip = isClientContactSession();

  const load = useCallback(async () => {
    if (!projectId || skip) return;
    const mine = ++seq.current;
    try {
      const r = await apiGet(`/api/comment-seen?projectId=${encodeURIComponent(projectId)}`, { fresh: true });
      if (mine !== seq.current) return;           // a newer read is on its way
      setItems(r && Array.isArray(r.items) ? r.items : []);
      setReadErr('');
    } catch (e) {
      if (mine !== seq.current) return;
      if (isAuthEnd(e)) { setItems([]); setReadErr(''); return; }
      setReadErr(READ_FAILED);
      console.warn('[DropPin] new replies read failed:', (e && e.message) || e);
    }
  }, [projectId, skip]);

  useEffect(() => { setItems([]); setReadErr(''); load(); }, [load]);
  useRereads(load);

  const byDefect = useMemo(() => new Map(items.map((x) => [Number(x.defectId), x])), [items]);
  return { items, byDefect, readErr };
}

// Filled, so it reads apart from the outlined Question chip beside it.
export function NewReplyChip({ count }) {
  if (!count) return null;
  return (
    <span
      data-testid="new-reply-chip"
      title="A reply you have not read yet — open the item to read it"
      style={{
        flexShrink: 0,
        display: 'inline-flex', alignItems: 'center', gap: 3,
        fontSize: 10, fontWeight: 700,
        padding: '2px 7px', borderRadius: 999,
        background: theme.brand, color: btnPrimary.color,
        border: `1px solid ${theme.brand}`,
        whiteSpace: 'nowrap',
      }}
    >
      <MessageSquareDot size={11} strokeWidth={2.4} aria-hidden="true" />
      {count === 1 ? 'New reply' : `${count} new replies`}
    </span>
  );
}

const noticeBox = {
  padding: '10px 12px', background: theme.bg, border: `1px solid ${theme.brand}`,
  color: theme.text, borderRadius: 6, fontSize: 13,
};
const compactBtn = { ...btnSecondary, padding: '4px 10px', fontSize: 12 };

/**
 * The register's line: how many items in this report have replies this
 * person has not read, a toggle to show only them, and — when new replies sit
 * on items of another report in the project — a way there.
 */
export function RegisterNewReplies({ count, onlyThese, onToggle, elsewhere, onOpenReport, readErr }) {
  if (readErr && !count && !elsewhere.count) {
    return <div style={{ marginTop: 14, fontSize: 12, color: theme.textLight }}>{readErr}</div>;
  }
  if (!count && !elsewhere.count) return null;
  return (
    <div data-testid="register-new-replies" role="status" style={{ ...noticeBox, marginTop: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <MessageSquareDot size={16} aria-hidden="true" style={{ color: theme.brand, flexShrink: 0 }} />
        <span style={{ flex: '1 1 220px', minWidth: 0 }}>
          {count > 0 && (
            <>
              <strong>{plural(count, 'item has', 'items have')} new replies</strong>
              {' '}you have not read.
            </>
          )}
          {count > 0 && elsewhere.count > 0 && ' '}
          {elsewhere.count > 0 && (
            <span style={{ color: count > 0 ? theme.textLight : theme.text }}>
              {count > 0 ? '' : <strong>New replies </strong>}
              {count > 0 ? `${plural(elsewhere.count, 'more item', 'more items')} on another report.` : `on ${plural(elsewhere.count, 'item', 'items')} in another report of this project.`}
            </span>
          )}
        </span>
        {count > 0 && (
          <button type="button" data-testid="new-replies-only" aria-pressed={onlyThese} onClick={onToggle} style={compactBtn}>
            {onlyThese ? 'Show all items' : 'Show only these'}
          </button>
        )}
        {elsewhere.count > 0 && elsewhere.reportId && (
          <button type="button" data-testid="new-replies-elsewhere" onClick={() => onOpenReport(elsewhere.reportId)} style={compactBtn}>
            Open that report
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * The Projects dashboard line: every project with replies this person has
 * not read, each a button into the report holding the newest one.
 */
export function NewRepliesNotice({ onOpen }) {
  const [projects, setProjects] = useState([]);
  const [readErr, setReadErr] = useState('');
  const seq = useRef(0);
  const skip = isClientContactSession();

  const load = useCallback(async () => {
    if (skip) return;
    const mine = ++seq.current;
    try {
      const r = await apiGet('/api/comment-seen', { fresh: true });
      if (mine !== seq.current) return;
      setProjects(r && Array.isArray(r.projects) ? r.projects.filter((p) => p.replies > 0) : []);
      setReadErr('');
    } catch (e) {
      if (mine !== seq.current) return;
      if (isAuthEnd(e)) { setProjects([]); setReadErr(''); return; }
      setReadErr(READ_FAILED);
      console.warn('[DropPin] new replies read failed:', (e && e.message) || e);
    }
  }, [skip]);

  useEffect(() => { load(); }, [load]);
  useRereads(load);

  if (readErr) {
    return <div style={{ fontSize: 12, color: theme.textLight, marginBottom: 12 }}>{readErr}</div>;
  }
  if (!projects.length) return null;
  const total = projects.reduce((n, p) => n + p.replies, 0);

  return (
    <div data-testid="new-replies-notice" role="status" style={{ ...noticeBox, marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
        <MessageSquareDot size={16} aria-hidden="true" style={{ color: theme.brand, flexShrink: 0, marginTop: 1 }} />
        <span style={{ flex: '1 1 0', minWidth: 0 }}>
          <strong>{plural(total, 'new reply', 'new replies')}</strong>
          {' '}you have not read, on {plural(projects.length, 'project', 'projects')}.
        </span>
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
        {projects.map((p) => (
          <button key={p.projectId} type="button" onClick={() => onOpen(p.projectId, p.reportId)} style={compactBtn}>
            {p.projectName || `Project ${p.projectId}`} · {p.replies}
          </button>
        ))}
      </div>
    </div>
  );
}
